import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { EServiceLine, serviceLinesRequiringContractId } from '../reducers/jbpGeneralSlice';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormButton } from '@portal_components/components/FormButton';
import { useUserInfo } from '@/hooks/useUserInfo';
import { makeMpanChecks, normalizeMpan } from '@portal_components/validation/validationUtils';
import { useIsEnv } from '@/hooks/useIsEnv';
import { selectServiceLines, selectStandingDataLoaded } from '@/reducers/standingDataSlice';
import { useAppSelector } from '@/store';
import { UserPermissions } from '../../../APIDocs/apiTypes';
import { Title } from '@portal_components/components/Title';

type MpanSearchFormValues = {
  mpan: string;
  serviceLine: EServiceLine;
};
export type PermissionKey = keyof UserPermissions;

export interface ServiceLine {
  contractTypeId: number;
  contractTypeDesc: string;
  contractTypeName?: string;
  active: string;
  permissionKey?: string | null;
}

const makeValidationSchema = (isLocalHost: boolean): Yup.ObjectSchema<MpanSearchFormValues> =>
  Yup.object().shape({
    mpan: Yup.string()
      .transform(value => (value ? normalizeMpan(value) : value))
      .required('MPAN is required')
      .matches(/^[0-9]{13}$/, 'MPAN must be 13 digits')
      .test('mpan-checksum', 'MPAN checksum is invalid', makeMpanChecks(isLocalHost)),
    serviceLine: Yup.string().required('Service Line is required').oneOf(Object.values(EServiceLine))
  });

export const Search = () => {
  const { canAccess } = useUserInfo();
  const { isLocalHost } = useIsEnv();

  const serviceLines = useAppSelector(selectServiceLines);
  const standingDataLoaded = useAppSelector(selectStandingDataLoaded);

  const navigate = useNavigate();

  type ServiceLineConfigItem = {
    value: string;
    title: string;
    active: boolean;
  };

  const serviceLineConfig: ServiceLineConfigItem[] =
    serviceLines
      ?.filter(
        serviceLine =>
          serviceLine.active === 'Y' &&
          typeof serviceLine.permissionKey === 'string' &&
          serviceLine.permissionKey.trim() !== '' &&
          typeof serviceLine.contractTypeName === 'string' &&
          serviceLine.contractTypeName.trim() !== ''
      )
      .map(
        (serviceLine: ServiceLine): ServiceLineConfigItem => ({
          value: serviceLine.contractTypeName ?? '',
          title: serviceLine.contractTypeDesc,
          active: serviceLine?.permissionKey ? canAccess(serviceLine.permissionKey as PermissionKey) : false
        })
      )
      .sort((a: ServiceLineConfigItem, b: ServiceLineConfigItem) => a.title.localeCompare(b.title)) ?? [];

  const submitSearch = ({ mpan, serviceLine }: MpanSearchFormValues) => {
    if (canAccess('canViewContractSelect') === true && serviceLine !== undefined && serviceLinesRequiringContractId.includes(serviceLine)) {
      navigate(`/contract/${mpan}/${serviceLine}`);
      return;
    }
    sessionStorage.removeItem('lastAttemptedUrl');
    navigate(`/site/${mpan}/${serviceLine}`);
  };

  const methods = useForm<MpanSearchFormValues>({
    values: {
      mpan: '',
      serviceLine: '' as EServiceLine // just to stop the uncontrolled to controlled warning
    },
    resolver: yupResolver<MpanSearchFormValues>(makeValidationSchema(isLocalHost) as any)
  });

  if (!standingDataLoaded) {
    return <div>Loading service lines…</div>;
  }

  return (
    <div id="jbp-search-screen">
      <div className="w-1/2 inline-block align-top">
        <div className="mx-10">
          <Title className="mt-7 mb-2" text="Welcome to the Stark Job Booking Portal" />
          <div className="max-w-[30em]">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(submitSearch)}>
                <FormTextInput<MpanSearchFormValues> title="MPAN" placeholder="Enter your site MPAN" name="mpan" mandatory />
                <FormDropdown<MpanSearchFormValues>
                  title="Service Line"
                  mandatory
                  placeholder="Please select the service line"
                  name="serviceLine"
                  elementsForDisplay={serviceLineConfig?.reduce<{ title: string; value: string }[]>((acc, curr) => {
                    if (!curr.active) return acc;
                    acc.push({ value: curr.value, title: curr.title });
                    return acc;
                  }, [])}
                />
                <div className="mt-2">
                  <FormButton type="submit" disabled={false} primary icon>
                    Search
                  </FormButton>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
      <div className="inline-block w-1/2 h-screen bg-center bg-origin-content bg-cover" style={{ backgroundImage: 'url(/searchImagery.png)' }}></div>
    </div>
  );
};
